import React, { useCallback, useEffect, useState } from 'react';
import { useEnterpriseManage } from './EnterpriseManageContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import EnterpriseRoleBadge from '../../components/enterprise/EnterpriseRoleBadge';
import {
  ENTERPRISE_CAPABILITY_META,
  capabilityLabels,
  emptyRoleDraft,
} from '../../utils/enterpriseRoles';
import {
  createEnterpriseRole,
  deleteEnterpriseRole,
  fetchEnterpriseRoles,
  updateEnterpriseRole,
} from '../../api/enterprises';

function RoleEditor({ draft, onChange, disabled }) {
  return (
    <div className="space-y-3">
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <Input
            label="Nom"
            value={draft.name}
            disabled={disabled}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Couleur</label>
          <input
            type="color"
            className="h-9 w-12 rounded border border-border shadow-sm cursor-pointer"
            value={draft.colorHex}
            disabled={disabled}
            onChange={(e) => onChange({ ...draft, colorHex: e.target.value })}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {ENTERPRISE_CAPABILITY_META.map((cap) => (
          <label
            key={cap.key}
            className="inline-flex items-center gap-2 text-sm cursor-pointer"
            title={cap.hint}
          >
            <input
              type="checkbox"
              checked={draft[cap.key] === true}
              disabled={disabled}
              onChange={(e) => onChange({ ...draft, [cap.key]: e.target.checked })}
            />
            {cap.label}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function EnterpriseRolesTab() {
  const { enterpriseId } = useEnterpriseManage();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState(emptyRoleDraft);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyRoleDraft);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchEnterpriseRoles(enterpriseId);
      setRoles(res?.data?.roles || []);
    } catch (e) {
      setError(e?.message || 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [enterpriseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const startEdit = (role) => {
    setEditingId(role.roleId);
    setEditDraft({
      name: role.name,
      colorHex: role.colorHex,
      crmAccess: role.crmAccess,
      crmManager: role.crmManager,
      hubupAccess: role.hubupAccess,
      hubupManager: role.hubupManager,
      teamManage: role.teamManage,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createDraft.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await createEnterpriseRole(enterpriseId, createDraft);
      setCreateDraft(emptyRoleDraft());
      setCreating(false);
      await load();
    } catch (e) {
      setError(e?.message || 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editDraft.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await updateEnterpriseRole(enterpriseId, editingId, editDraft);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (role.isSystemAdmin) return;
    if (!window.confirm(`Supprimer « ${role.name} » ?`)) return;
    try {
      await deleteEnterpriseRole(enterpriseId, role.roleId);
      await load();
    } catch (e) {
      setError(e?.message || 'Suppression impossible.');
    }
  };

  return (
    <div className="space-y-4 max-w-xl">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold text-foreground">Rôles</h1>
        <Button type="button" size="sm" variant="outline" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Fermer' : 'Ajouter'}
        </Button>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {creating ? (
        <form onSubmit={handleCreate} className="rounded-lg border border-border p-4 space-y-3">
          <RoleEditor draft={createDraft} onChange={setCreateDraft} disabled={saving} />
          <Button type="submit" size="sm" disabled={saving || !createDraft.name.trim()}>
            Créer
          </Button>
        </form>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : (
        <ul className="space-y-2">
          {roles.map((role) => (
            <li key={role.roleId} className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <EnterpriseRoleBadge role={role} />
                {!role.isSystemAdmin ? (
                  <div className="flex gap-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(role)}>
                      Modifier
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(role)}>
                      Supprimer
                    </Button>
                  </div>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                {capabilityLabels(role).join(' · ')}
              </p>
              {editingId === role.roleId ? (
                <div className="pt-2 space-y-2 border-t border-border">
                  <RoleEditor draft={editDraft} onChange={setEditDraft} disabled={saving} />
                  <div className="flex gap-2">
                    <Button type="button" size="sm" disabled={saving} onClick={() => void handleSaveEdit()}>
                      OK
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
